import React from 'react';
import PropTypes from 'prop-types';

import './LabeledField.less';

/**
 * Form field with an embedded label that is compatible with Redux Form.
 */
export const LabeledField = ({
  as, autoFocus, children, input, label, placeholder, type, meta: { touched, error }, 'aria-describedby': ariaDescribedBy, ...props
}) => {
  const Component = as;
  const id = `LabeledField_${input.name}`;

  // only show error in label and placeholder if field has been touched
  const showError = touched && error;
  const inputLabel = showError ? error : label;
  const inputPlaceholder = showError ? error : placeholder;

  // aria attributes for error state
  const ariaAttributes = {};

  if (showError) {
    ariaAttributes['aria-invalid'] = true;
    ariaAttributes['aria-describedby'] = `${input.name}_error ${ariaDescribedBy || ''}`;
  } else if (ariaDescribedBy) {
    ariaAttributes['aria-describedby'] = ariaDescribedBy;
  }

  return (
    <div className={`LabeledField labeled_field
      labeled_field_${input.name}
      ${Component.displayName || Component.name || Component}
      ${input.value ? 'filled' : 'empty'}
      ${showError ? 'error' : ''}`}
    >
      {Component === 'select' ? (
        <Component {...props} {...input} {...ariaAttributes} autoFocus={autoFocus} id={id}>
          { placeholder !== '' && (
            <option value="">
              {inputPlaceholder}
            </option>
          )}
          {children}
        </Component>
      ) : (
        <Component
          {...props}
          {...input}
          {...ariaAttributes}
          autoFocus={autoFocus}
          id={id}
          input={{
            ...input, ...ariaAttributes, autoFocus, placeholder: inputPlaceholder, type,
          }}
          placeholder={inputPlaceholder}
          type={type}
        />
      )}
      <label htmlFor={id}>
        {inputLabel}
      </label>
      <span className={`labeled_field_error ${input.name}_error`} id={`${input.name}_error`} aria-live="polite">{showError && inputPlaceholder}</span>
    </div>
  );
};

LabeledField.propTypes = {
  /** HTML element (as a string) or component that will be rendered. */
  as: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.elementType,
  ]),

  /** Specifies if the field will have focus when it loads. */
  autoFocus: PropTypes.bool,

  /**
   * Array of elements that will be displayed as additional options if a
   * `<select/>` element is chosen for the `as` prop.
  */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  /**
   * Connects your input component to Redux whose props are destructured into
   * your `<input/>` component.
  */
  input: PropTypes.shape({
    /** Returns the name prop that you passed in. */
    name: PropTypes.string,
    /**
     * The value of this form field. It will default to `''` to ensure that the
     * input is controlled.
    */
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  }).isRequired,

  /** Text that describes the content for the input element. */
  label: PropTypes.string.isRequired,
  /** A hint to the user of what can be entered in the control. */
  placeholder: PropTypes.string,
  /** Value that will be used for the input's type attribute. */
  type: PropTypes.string,
  /** Value that will be used for the input's aria-describedby attribute. */
  'aria-describedby': PropTypes.string,

  /**
   * The props under the meta key are metadata about the state of this field
   * that redux-form is tracking for you.
  */
  meta: PropTypes.shape({
    /** The error for this field if its value is not passing validation. */
    error: PropTypes.string,
    /**
     * `true` if the field has been touched. By default this will be set when
     * the field is blurred.
    */
    touched: PropTypes.bool,
  }),
};

LabeledField.defaultProps = {
  as: 'input',
  autoFocus: false,
  children: null,
  meta: {
    error: null,
    touched: false,
  },
  placeholder: '',
  type: 'text',
  'aria-describedby': '',
};

export default LabeledField;
